<template>
  <div>
    <h1 class="title">Admin Users</h1>
    <div class="mx-10" v-if="tableDisplay">
      <v-row>
        <v-col md="3" class="ml-md-auto">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </div>
    <div class="mx-10" v-if="noAccountsFound">
      <h3 class="pa-4">NO ADMIN ACCOUNTS FOUND</h3>
    </div>
    <div align="center">
      <v-data-table
        v-if="tableDisplay"
        :headers="headers"
        :items="accountList"
        :search="search"
        class="elevation-1"
        :sort-by="['accountId']"
        :sort-desc="[false]"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        @page-count="pageCount = $event"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>Users</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>
            <v-spacer></v-spacer>
            <v-dialog v-model="newUserForm" max-width="500px" persistent>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  dark
                  class="mb-2"
                  v-bind="attrs"
                  v-on="on"
                >
                  Create New User
                </v-btn>
              </template>
              <!-- Create New User Form -->
              <v-card height="100%">
                <v-card-title class="justify-center">
                  <span class="headline">Create New User</span>
                </v-card-title>
                <v-card-text class="pb-0">
                  <v-form
                    ref="newUserForm"
                    v-model="newUserformValidity"
                    @submit.prevent="saveNewUser"
                  >
                    <v-container class="inputs-container">
                      <v-row>
                        <v-col cols="12" class="py-0">
                          <v-text-field
                            label="Name*"
                            v-model="newUser.name"
                            :rules="nameRules"
                            prepend-icon="mdi-account-circle"
                            required
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" class="py-0">
                          <v-text-field
                            label="Email*"
                            type="email"
                            v-model="newUser.email"
                            :rules="emailRules"
                            prepend-icon="mdi-email"
                            required
                            outlined
                            dense
                          >
                          </v-text-field>
                        </v-col>
                        <v-col cols="12" class="py-0">
                          <v-text-field
                            label="Password*"
                            :type="showPassword ? 'text' : 'password'"
                            v-model="newUser.password"
                            :rules="passwordRules"
                            prepend-icon="mdi-lock"
                            :append-icon="
                              showPassword ? 'mdi-eye' : 'mdi-eye-off'
                            "
                            @click:append="showPassword = !showPassword"
                            required
                            outlined
                            dense
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-form>
                </v-card-text>
                <v-card-actions class="pt-2 pb-6 px-4 justify-center">
                  <v-btn
                    color="error"
                    class="mr-4 mb-4"
                    align="left"
                    @click="closeNewUserForm"
                  >
                    CLOSE
                  </v-btn>
                  <v-btn
                    @click="dialogNewUserConfirm = true"
                    :disabled="!newUserformValidity"
                    color="#173862"
                    dark
                    class="mb-4"
                    align="center"
                  >
                    Save
                  </v-btn>
                  <ConfirmDialog
                    v-if="dialogNewUserConfirm"
                    :confirmDialogShow="dialogNewUserConfirm"
                    @confirm-dialog-close="dialogNewUserConfirm = false"
                    @confirm-dialog-save="saveNewUser"
                  />
                </v-card-actions>
              </v-card>
            </v-dialog>
            <!-- <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title class="text-h5">Are you sure you want to delete this item?</v-card-title>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDelete">Cancel</v-btn>
                <v-btn color="blue darken-1" text @click="deleteItemConfirm">OK</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog> -->
          </v-toolbar>
        </template>

        <template v-slot:[`item.disable`]="{ item }">
          <v-row align="center" justify="center" class="pa-0 ma-0">
            <v-col cols="12" sm="8" class="pa-0 ma-0">
              <v-switch
                v-model="item.disable"
                :false-value="true"
                :true-value="false"
                class="pa-0 ma-0"
                hide-details="false"
                @change="onToggleChange(item)"
              ></v-switch>
            </v-col>
          </v-row>
        </template>
      </v-data-table>

      <div class="text-center pt-2" v-if="accountList.length > itemsPerPage">
        <v-pagination v-model="page" :length="pageCount"></v-pagination>
      </div>
    </div>

    <v-snackbar v-model="snackbar">
      You have selected the Account Number: {{ currentItem }}
      <v-btn color="pink" text @click="snackbar = false"> Close </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import admin from "../api/adminOps";
import { authComputed } from "../store/helpers.js";
import { mapMutations } from "vuex";

export default {
  components: {
    ConfirmDialog: () => import("@/components/ConfirmDialog"),
  },
  async created() {
    let adminAccounts = this.$store.getters["users/getAdminAccounts"];
    if (adminAccounts) {
      this.accountList = adminAccounts;
      this.tableDisplay = true;
    } else {
      // API call to fetch super admin users list
      await admin
        .fetchAdminUsers()
        .then((response) => {
          let adminAccounts = response.value ? response.value.data : [];
          if (adminAccounts.length) {
            this.$store.commit("users/SET_ADMIN_ACCOUNTS", adminAccounts);
            this.accountList = adminAccounts;
            this.tableDisplay = true;
          } else {
            this.noAccountsFound = true;
          }
        })
        .catch((error) => {
          console.error(
            `[AdminAccountDashboard] - fetchSuperAdmins - error ${JSON.stringify(
              error
            )}`
          );
          this.showSnack(
            `Fetching super admin user got failed! Please try again!`,
            true
          );
        });
    }
  },
  data() {
    return {
      accountList: [],
      currentItem: "",
      dialogNewUserConfirm: false,
      emailRules: [
        (value) => !!value || "Email is required.",
        (value) => value.indexOf("@") !== 0 || "Email should have a username.",
        (value) => value.includes("@") || "Email should include an @ symbol.",
        (value) =>
          value.lastIndexOf(".") - value.indexOf("@") > 1 ||
          "Email should include a period symbol.",
        (value) =>
          value.lastIndexOf(".") <= value.length - 3 ||
          "Email should contain a valid domain extension.",
      ],
      headers: [
        { text: "Name", value: "adminName", align: "start", sortable: false },
        { text: "Email", value: "adminEmail" },
        { text: "Role", value: "role" },
        { text: "Status", value: "disable" },
      ],
      itemsPerPage: 8, // 8 - when using external pagination
      nameRules: [(value) => !!value || "Name is required."],
      newUser: {
        name: "",
        email: "",
        password: "",
      },
      newUserForm: false,
      newUserformValidity: false,
      noAccountsFound: false,
      page: 1,
      pageCount: 0,
      passwordRules: [
        (value) => !!value || "Password is required.",
        (value) => {
          const pattern =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/;
          return (
            pattern.test(value) ||
            "Min. 8 characters with at least one capital letter, a number and a special character."
          );
        },
      ],
      search: "",
      snackbar: false,
      showPassword: false,
      showConfirmPassword: false,
      tableDisplay: false,
    };
  },
  computed: {
    ...authComputed,
  },
  methods: {
    closeNewUserForm() {
      this.newUserForm = false;
      this.newUser = {
        name: "",
        email: "",
        password: "",
      };
      this.resetNewUserValidation();
    },
    ...mapMutations({
      setSnack: "setSnack",
    }),
    onToggleChange(item) {
      let payload = {
        recordId: item.id,
        disable: item.disable,
      };
      admin
        .changeUserStatus(payload)
        .then(async (response) => {
          this.showSnack(
            ` ${
              item.disable ? "Disbaled" : "Enabled"
            } Admin User Successfully!`,
            false
          );

          // Admin accounts modification
          let adminAccounts = this.$store.getters["users/getAdminAccounts"];
          await adminAccounts.forEach((element) => {
            if (element.id === item.id) {
              Object.assign(element, item);
            }
          });
          //commit SET_ADMIN_ACCOUNTS keep updated data without refresh
          this.$store.commit("users/SET_ADMIN_ACCOUNTS", adminAccounts);
        })
        .catch((error) => {
          console.error(
            `[AdminAccountDashboard] - onToggleChange - error ${JSON.stringify(
              error
            )}`
          );
          item.disable = !item.disable;
          this.showSnack(`Account status updation failed.`, true);
        });
    },
    resetNewUserValidation() {
      this.$refs.newUserForm.resetValidation();
    },
    saveNewUser() {
      this.newUserForm = false;
      this.dialogNewUserConfirm = false;

      let adminData = this.$store.getters["users/getAdminData"];
      let payload = {
        strict: true,
        value: {
          op: "CREATEUSER",
          sessionId: adminData.sessionId,
          data: {
            username: this.newUser.email,
            password: this.newUser.password,
            adminName: this.newUser.name,
            role: "admin",
            // "accountId": this.newUser.email
            accountId: adminData.accountId, //TO-DO pending field
          },
        },
      };
      admin
        .adminOps(payload)
        .then((response) => {
          this.showSnack("Saved New User Successfully!", false);
          // Admin accounts modification
          let adminAccounts = this.$store.getters["users/getAdminAccounts"];
          adminAccounts.push({
            adminEmail: this.newUser.email,
            adminName: this.newUser.name,
            disable: response.value.data.disable,
            id: response.value.data.id,
            role: "admin",
          });
          //commit SET_ADMIN_ACCOUNTS keep updated data without refresh
          this.$store.commit("users/SET_ADMIN_ACCOUNTS", adminAccounts);
          this.accountList = adminAccounts;
          // New User Form varaibles and validations to initial state
          this.newUserForm = false;
          this.newUser = {
            name: "",
            email: "",
            password: "",
          };
          this.resetNewUserValidation();
        })
        .catch((error) => {
          console.error(
            `[AdminSettings] - saveNewUser - error ${JSON.stringify(error)}`
          );
          this.newUserForm = false;
          this.showSnack("Saving New User failed!", true);
          // New User Form varaibles and validations to initial state
          this.newUser = {
            name: "",
            email: "",
            password: "",
          };
          this.resetNewUserValidation();
        });
    },
    showSnack: function (snack, isError) {
      const setSnackObj = {
        message: snack,
        isError: !!isError,
      };
      this.setSnack(setSnackObj);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table {
  max-width: 60% !important;
}
::v-deep .v-data-table th {
  font-size: 1.1rem !important;
  font-weight: bold !important;
}
::v-deep .v-data-table td {
  font-size: 0.9rem !important;
}

::v-deep .v-select__selections {
  width: 1rem;
}
.v-snack__content {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-right: 16px;
  padding-left: 16px;
}
</style>

